import { getBackendUrl } from '../config/api';
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  Calendar, 
  Plus, 
  Trash2, 
  Filter,
  CalendarDays,
  Sparkles,
  AlertTriangle
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { toast } from 'sonner';

const BACKEND_URL = getBackendUrl();
const API = `${BACKEND_URL}/api`;

const HolidayManagement = () => {
  const [holidays, setHolidays] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Filters
  const [yearFilter, setYearFilter] = useState(new Date().getFullYear());
  const [typeFilter, setTypeFilter] = useState('all');
  
  // Add holiday modal
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDateRange, setIsDateRange] = useState(false); // Toggle for date range mode
  const [formData, setFormData] = useState({
    name: '',
    date: '',
    start_date: '',
    end_date: '',
    holiday_type: 'festival',
    description: '',
    is_recurring: true,
    is_staff_holiday: false
  });

  useEffect(() => {
    fetchHolidays();
  }, [yearFilter]);

  const fetchHolidays = async () => {
    try {
      const params = yearFilter ? `?year=${yearFilter}` : '';
      const response = await axios.get(`${API}/holidays${params}`);
      setHolidays(response.data);
    } catch (error) {
      console.error('Error fetching holidays:', error);
      toast.error('Failed to load holidays');
    } finally {
      setLoading(false);
    }
  };

  const handleAddHoliday = async (e) => {
    e.preventDefault();
    
    try {
      if (isDateRange) {
        // Validate date range
        if (!formData.start_date || !formData.end_date) {
          toast.error('Please select both start and end dates');
          return;
        }
        
        const startDate = new Date(formData.start_date);
        const endDate = new Date(formData.end_date);
        
        if (endDate < startDate) {
          toast.error('End date must be after start date');
          return;
        }
        
        // Calculate number of days
        const daysDiff = Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24)) + 1;
        
        // Create holidays for date range
        const rangeData = {
          name: formData.name,
          start_date: formData.start_date,
          end_date: formData.end_date,
          holiday_type: formData.holiday_type,
          description: formData.description,
          is_recurring: formData.is_recurring,
          is_staff_holiday: formData.is_staff_holiday
        };
        
        const response = await axios.post(`${API}/holidays/range`, rangeData);
        toast.success(`${response.data.length} holidays added successfully (${daysDiff} days)`);
      } else {
        // Create single holiday
        const singleData = {
          name: formData.name,
          date: formData.date,
          holiday_type: formData.holiday_type,
          description: formData.description,
          is_recurring: formData.is_recurring,
          is_staff_holiday: formData.is_staff_holiday
        };
        
        await axios.post(`${API}/holidays`, singleData);
        toast.success('Holiday added successfully');
      }
      
      setIsModalOpen(false);
      setIsDateRange(false);
      setFormData({
        name: '',
        date: '',
        start_date: '',
        end_date: '',
        holiday_type: 'festival',
        description: '',
        is_recurring: true,
        is_staff_holiday: false
      });
      fetchHolidays();
    } catch (error) {
      console.error('Error adding holiday:', error);
      toast.error(error.response?.data?.detail || 'Failed to add holiday');
    }
  };

  const handleDeleteHoliday = async (holidayId) => {
    if (window.confirm('Are you sure you want to delete this holiday?')) {
      try {
        await axios.delete(`${API}/holidays/${holidayId}`);
        toast.success('Holiday deleted successfully');
        fetchHolidays();
      } catch (error) {
        console.error('Error deleting holiday:', error);
        toast.error('Failed to delete holiday');
      }
    }
  };

  const handleStaffHolidayToggle = async (holidayId, isStaffHoliday) => {
    try {
      await axios.patch(`${API}/holidays/${holidayId}`, {
        is_staff_holiday: isStaffHoliday
      });
      toast.success(`Holiday ${isStaffHoliday ? 'marked as' : 'unmarked as'} staff holiday`);
      fetchHolidays();
    } catch (error) {
      console.error('Error updating staff holiday status:', error);
      toast.error('Failed to update staff holiday status');
    }
  };

  const generateFutureHolidays = async (year) => {
    try {
      setLoading(true);
      const response = await axios.post(`${API}/holidays/generate-future/${year}`);
      
      const { count, skipped_variable_holidays } = response.data;
      
      if (count > 0) {
        toast.success(`Generated ${count} fixed-date holidays for ${year}`, {
          description: skipped_variable_holidays?.length > 0 
            ? `Variable holidays like ${skipped_variable_holidays.slice(0,2).join(', ')} need manual entry`
            : 'Only fixed-date holidays (Republic Day, Christmas, etc.) generated'
        });
      } else {
        toast.warning(`No new holidays generated for ${year}`, {
          description: 'All fixed-date holidays may already exist. Variable holidays need manual entry.'
        });
      }
      
      fetchHolidays();
    } catch (error) {
      console.error('Error generating holidays:', error);
      toast.error('Failed to generate holidays');
    } finally {
      setLoading(false);
    }
  };

  const getTypeColor = (type) => {
    switch (type) {
      case 'national':
        return 'bg-red-100 text-red-800';
      case 'religious':
        return 'bg-purple-100 text-purple-800';
      case 'festival':
        return 'bg-orange-100 text-orange-800';
      case 'state':
        return 'bg-blue-100 text-blue-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const filteredHolidays = holidays.filter(holiday => {
    if (typeFilter !== 'all' && holiday.holiday_type !== typeFilter) {
      return false;
    }
    return true;
  });

  const groupHolidaysByMonth = () => {
    const grouped = {};
    filteredHolidays.forEach(holiday => {
      const date = new Date(holiday.date);
      const monthKey = `${date.getFullYear()}-${date.getMonth()}`;
      const monthName = date.toLocaleDateString('en-US', { year: 'numeric', month: 'long' });
      
      if (!grouped[monthKey]) {
        grouped[monthKey] = {
          name: monthName,
          holidays: []
        };
      }
      grouped[monthKey].holidays.push(holiday);
    });
    
    // Sort holidays within each month by date
    Object.values(grouped).forEach(month => {
      month.holidays.sort((a, b) => new Date(a.date) - new Date(b.date));
    });
    
    return grouped;
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      weekday: 'long',
      day: 'numeric',
      month: 'short'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  const groupedHolidays = groupHolidaysByMonth();
  const currentYear = new Date().getFullYear();

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2 flex items-center">
            <CalendarDays className="w-8 h-8 mr-3 text-blue-600" />
            Holiday Management
          </h1>
          <p className="text-gray-600">Manage institute holidays - automatically excluded from timetable scheduling</p>
        </div>
        
        <div className="flex items-center space-x-3">
          <Button 
            onClick={() => setIsModalOpen(true)}
            className="btn-primary flex items-center" 
            data-testid="add-holiday-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Holiday
          </Button>
        </div>
      </div>

      {/* Filters & Controls */}
      <Card className="card-enhanced">
        <CardContent className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Year</label>
              <Select value={yearFilter.toString()} onValueChange={(value) => setYearFilter(parseInt(value))}>
                <SelectTrigger data-testid="year-filter">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="2025">2025</SelectItem>
                  <SelectItem value="2026">2026</SelectItem>
                  <SelectItem value="2027">2027</SelectItem>
                  <SelectItem value="2028">2028</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Type</label>
              <Select value={typeFilter} onValueChange={setTypeFilter}>
                <SelectTrigger data-testid="type-filter">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  <SelectItem value="national">National</SelectItem>
                  <SelectItem value="religious">Religious</SelectItem>
                  <SelectItem value="festival">Festival</SelectItem>
                  <SelectItem value="state">State</SelectItem>
                  <SelectItem value="institutional">Institutional</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-end">
              <Button
                variant="outline"
                onClick={() => generateFutureHolidays(yearFilter + 1)}
                className="flex items-center w-full"
                data-testid="generate-holidays-btn"
                disabled={yearFilter >= currentYear + 3}
              >
                <Sparkles className="w-4 h-4 mr-2" />
                Generate {yearFilter + 1}
              </Button>
            </div>

            <div className="flex items-end justify-end">
              <div className="text-right">
                <div className="text-sm text-gray-600">Total Holidays</div>
                <div className="text-2xl font-bold text-blue-600">{filteredHolidays.length}</div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Holiday Calendar Display */}
      {Object.keys(groupedHolidays).length === 0 ? (
        <Card className="card-enhanced">
          <CardContent className="p-12 text-center">
            <Calendar className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              No Holidays Found
            </h3>
            <p className="text-gray-600 mb-6">
              No holidays found for {yearFilter}. You can add holidays manually or generate them from previous years.
            </p>
            <div className="flex justify-center space-x-3">
              <Button onClick={() => setIsModalOpen(true)} className="btn-primary">
                <Plus className="w-4 h-4 mr-2" />
                Add Holiday
              </Button>
              {yearFilter > 2025 && (
                <Button 
                  variant="outline"
                  onClick={() => generateFutureHolidays(yearFilter)}
                >
                  <Sparkles className="w-4 h-4 mr-2" />
                  Generate from 2025
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-6">
          {Object.entries(groupedHolidays).map(([monthKey, monthData]) => (
            <Card key={monthKey} className="card-enhanced">
              <CardHeader className="pb-4">
                <CardTitle className="flex items-center text-lg">
                  <Calendar className="w-5 h-5 mr-2 text-blue-600" />
                  {monthData.name}
                  <Badge variant="outline" className="ml-4">
                    {monthData.holidays.length} holiday{monthData.holidays.length !== 1 ? 's' : ''}
                  </Badge>
                </CardTitle>
              </CardHeader>
              
              <CardContent className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {monthData.holidays.map((holiday) => (
                  <div
                    key={holiday.id}
                    className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50 transition-colors"
                    data-testid={`holiday-${holiday.id}`}
                  >
                    <div className="flex-1">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center space-x-2">
                          <h4 className="font-medium text-gray-900">{holiday.name}</h4>
                          <Badge className={getTypeColor(holiday.holiday_type)}>
                            {holiday.holiday_type}
                          </Badge>
                          {holiday.is_staff_holiday && (
                            <Badge className="bg-orange-100 text-orange-700 border-orange-300">
                              <Sparkles className="w-3 h-3 mr-1" />
                              Staff Holiday
                            </Badge>
                          )}
                        </div>
                        
                        {/* Staff Holiday Checkbox */}
                        <div className="flex items-center space-x-2">
                          <input
                            type="checkbox"
                            checked={holiday.is_staff_holiday || false}
                            onChange={(e) => handleStaffHolidayToggle(holiday.id, e.target.checked)}
                            className="rounded border-gray-300 text-orange-600 focus:ring-orange-500 w-4 h-4"
                            title="Mark as Staff Holiday"
                          />
                          <label className="text-xs text-gray-600">Staff Holiday</label>
                        </div>
                      </div>
                      
                      <div className="text-sm text-gray-600 mb-1">
                        {formatDate(holiday.date)}
                      </div>
                      
                      {holiday.description && (
                        <div className="text-xs text-gray-500">
                          {holiday.description}
                        </div>
                      )}
                    </div>
                    
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleDeleteHoliday(holiday.id)}
                      className="p-2 h-8 w-8 text-red-600 hover:text-red-700 hover:bg-red-50"
                      data-testid={`delete-holiday-${holiday.id}`}
                    >
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </div>
                ))}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Add Holiday Modal */}
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle data-testid="holiday-modal-title">
              Add New Holiday
            </DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleAddHoliday} className="space-y-4">
            <div className="form-group">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Holiday Name *
              </label>
              <Input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="form-input"
                placeholder="Enter holiday name"
                required
                data-testid="holiday-name-input"
              />
            </div>

            {/* Date Range Toggle */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={isDateRange}
                  onChange={(e) => setIsDateRange(e.target.checked)}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 w-4 h-4"
                  id="date-range-toggle"
                />
                <label htmlFor="date-range-toggle" className="text-sm text-gray-700 flex items-center cursor-pointer">
                  <Calendar className="w-4 h-4 text-blue-600 mr-1" />
                  <span className="font-medium text-blue-700">Date Range Mode</span> - For multi-day holidays
                </label>
              </div>
              <p className="text-xs text-blue-600 mt-1 ml-6">
                Enable this to add holidays spanning multiple days (e.g., 15-day vacation)
              </p>
            </div>

            {/* Conditional Date Inputs */}
            {isDateRange ? (
              <div className="grid grid-cols-2 gap-4">
                <div className="form-group">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Start Date *
                  </label>
                  <Input
                    type="date"
                    value={formData.start_date}
                    onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                    className="form-input"
                    required={isDateRange}
                    data-testid="holiday-start-date-input"
                  />
                </div>
                <div className="form-group">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    End Date *
                  </label>
                  <Input
                    type="date"
                    value={formData.end_date}
                    onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                    className="form-input"
                    required={isDateRange}
                    min={formData.start_date} // End date must be after start date
                    data-testid="holiday-end-date-input"
                  />
                </div>
              </div>
            ) : (
              <div className="form-group">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Date *
                </label>
                <Input
                  type="date"
                  value={formData.date}
                  onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                  className="form-input"
                  required={!isDateRange}
                  data-testid="holiday-date-input"
                />
              </div>
            )}

            <div className="form-group">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Type *
              </label>
              <Select 
                value={formData.holiday_type} 
                onValueChange={(value) => setFormData({ ...formData, holiday_type: value })}
              >
                <SelectTrigger data-testid="holiday-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="national">National Holiday</SelectItem>
                  <SelectItem value="religious">Religious Festival</SelectItem>
                  <SelectItem value="festival">Festival</SelectItem>
                  <SelectItem value="state">State Holiday</SelectItem>
                  <SelectItem value="institutional">Institutional</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="form-group">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Description
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="form-input resize-none"
                rows="2"
                placeholder="Optional description"
                data-testid="holiday-description-input"
              />
            </div>

            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                checked={formData.is_recurring}
                onChange={(e) => setFormData({ ...formData, is_recurring: e.target.checked })}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                data-testid="holiday-recurring-checkbox"
              />
              <label className="text-sm text-gray-700">
                Recurring holiday (apply to future years)
              </label>
            </div>

            <div className="bg-orange-50 border border-orange-200 rounded-lg p-3 mt-4">
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={formData.is_staff_holiday}
                  onChange={(e) => setFormData({ ...formData, is_staff_holiday: e.target.checked })}
                  className="rounded border-gray-300 text-orange-600 focus:ring-orange-500 w-4 h-4"
                  data-testid="holiday-staff-holiday-checkbox"
                />
                <label className="text-sm text-gray-700 flex items-center">
                  <Sparkles className="w-4 h-4 text-orange-600 mr-1" />
                  <span className="font-medium text-orange-700">Staff Holiday</span> - Staff not required to work
                </label>
              </div>
              <p className="text-xs text-orange-600 mt-1 ml-6">
                Check this if staff should have the day off (affects leave calculations and payroll)
              </p>
            </div>
            
            <div className="flex space-x-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => setIsModalOpen(false)}
                className="flex-1"
                data-testid="cancel-holiday-btn"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="flex-1 btn-primary"
                data-testid="save-holiday-btn"
              >
                Add Holiday
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Holiday System Notices */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Holiday Impact Notice */}
        <Card className="border-blue-200 bg-blue-50">
          <CardContent className="p-4">
            <div className="flex items-start space-x-3">
              <AlertTriangle className="w-5 h-5 text-blue-600 mt-0.5" />
              <div className="text-sm text-blue-800">
                <p className="font-medium mb-1">Holiday Impact on Scheduling</p>
                <p>All holidays are automatically excluded when generating course sessions in the Smart Timetable Builder. Sessions will skip holiday dates and continue on the next available day.</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Variable Date Warning */}
        <Card className="border-orange-200 bg-orange-50">
          <CardContent className="p-4">
            <div className="flex items-start space-x-3">
              <AlertTriangle className="w-5 h-5 text-orange-600 mt-0.5" />
              <div className="text-sm text-orange-800">
                <p className="font-medium mb-1">Variable Date Holidays</p>
                <p>Holidays like Holi, Diwali, Eid have different dates each year (lunar calendar). Only fixed holidays like Republic Day auto-generate for future years.</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default HolidayManagement;